import React from 'react';
import { useRouter } from 'next/compat/router';

import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { HeadingDescription } from 'src/components/heading-description/heading-description';
import useUI from 'hooks/use-ui';

import { findMatchingCategorySubheaderText, getCategoryToMatch } from './category-page-subheaders';

/**
 * Resolves the validated sub-header text (ECOMM-13289) for the current
 * category page, or `undefined` when there's no config / an invalid
 * config, in which case callers must render nothing extra and leave
 * today's H1-only layout untouched.
 *
 * Scoped to category pages: callers (category.jsx) only invoke this from
 * the category page route -- search, specials, brands, and collections
 * never call it, so they can't receive a subheader.
 */
export function useCategoryPageSubheaderText(): string | undefined {
  const router = useRouter();
  const { iterables } = useCommerceComponents();
  const { isKiosk, isStoreFront } = useUI();

  // Product decision: SDK category subheaders are storefront-only and only
  // belong to top-level category routes. Pagination intentionally does not
  // affect this content; it remains visible while the product list paginates.
  if (!router?.isReady || !isStoreFront || isKiosk || typeof router.query.subcategory === 'string') {
    return undefined;
  }

  const entries = iterables.CategoryPageSubheaders();
  const categoryToMatch = getCategoryToMatch(router.query);

  return findMatchingCategorySubheaderText(entries, categoryToMatch);
}

type CategorySubheaderProps = {
  text: string;
};

/**
 * Renders directly under the category H1 (ECOMM-13289). Reuses the same
 * "heading + sub-copy" presentation contract as collection descriptions
 * (`Typography.Body size='large'`, `--dcm-collections-description-color`,
 * `margin-top: 8px`) via the shared `HeadingDescription` primitive rather
 * than a category-specific styled component/token.
 */
export function CategorySubheader({ text }: CategorySubheaderProps): JSX.Element {
  return (
    <HeadingDescription size='large' tag='p' data-testid='category-subheader'>
      {text}
    </HeadingDescription>
  );
}
